// 글로벌 + 사이드바 키보드 단축키 hook (U41, U53)
//
// 지원 단축키:
//   g h      → 홈(Explorer 루트 /) 이동
//   g t      → 태그 관리(/tags) 이동
//   /        → 검색바 focus (CustomEvent 'locker:focus-search' 발행)
//   Esc      → 모달·패널·검색 취소 (CustomEvent 'locker:close-overlay' 발행)
//   ?        → 단축키 모달 open (CustomEvent 'locker:open-shortcuts-modal' 발행, 실제 모달은 U55)
//   Cmd+Z    → Undo (CustomEvent 'locker:undo' 발행 — UndoStack이 구독)
//
// 비활성 조건: input·textarea·select·contenteditable 포커스 중에는 단축키 무시.
// chord 패턴: 'g' 키 입력 후 1초(CHORD_TIMEOUT_MS) 안에 두 번째 키 입력 시 조합 인식.
// Cmd+Z 예외: input·textarea 포커스 중에는 OS 기본 undo가 동작하도록 비활성.

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// chord 대기 시간 (ms)
const CHORD_TIMEOUT_MS = 1000;

// 이벤트 이름 상수 — 다른 컴포넌트에서 listen 시 이 이름을 사용
export const SHORTCUT_EVENT_FOCUS_SEARCH = 'locker:focus-search';
export const SHORTCUT_EVENT_CLOSE_OVERLAY = 'locker:close-overlay';
export const SHORTCUT_EVENT_OPEN_SHORTCUTS_MODAL = 'locker:open-shortcuts-modal';
/** Undo 이벤트 — Cmd+Z 입력 시 발행. UndoStackMount가 구독하여 undoLast() 호출. */
export const SHORTCUT_EVENT_UNDO = 'locker:undo';

/**
 * 주어진 엘리먼트(또는 document.activeElement)가 텍스트 입력 영역인지 확인.
 * 입력 영역이면 true → 단축키 비활성.
 *
 * @param el 검사할 엘리먼트. 생략 시 document.activeElement 사용.
 */
export function isInputFocused(el?: Element | null): boolean {
  const target = el !== undefined ? el : document.activeElement;
  if (!target) return false;
  const tag = target.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  // isContentEditable: 표준 DOM 속성. jsdom에서 contentEditable='true'로 설정한 요소도 포함.
  // getAttribute('contenteditable') === 'true'를 fallback으로 병행 체크.
  const htmlEl = target as HTMLElement;
  if (htmlEl.isContentEditable || htmlEl.getAttribute('contenteditable') === 'true') return true;
  return false;
}

/**
 * 글로벌 키보드 단축키 hook.
 * RequireAuth 영역 안에서 마운트해야 useNavigate를 사용할 수 있다.
 */
export function useGlobalShortcuts(): void {
  const navigate = useNavigate();

  // chord 상태 — 'g' 키 누른 후 두 번째 키 대기 중인지
  const chordActiveRef = useRef(false);
  const chordTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    /**
     * chord 대기 해제 (타임아웃 또는 두 번째 키 처리 후 호출).
     */
    const clearChord = () => {
      chordActiveRef.current = false;
      if (chordTimerRef.current !== null) {
        clearTimeout(chordTimerRef.current);
        chordTimerRef.current = null;
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+Z (Mac: metaKey+z, Win/Linux: ctrlKey+z) — Undo 전용 처리
      // input·textarea 포커스 중에는 OS 기본 undo 동작하도록 우선 통과
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey && !e.altKey) {
        if (!isInputFocused()) {
          e.preventDefault();
          window.dispatchEvent(new CustomEvent(SHORTCUT_EVENT_UNDO));
        }
        // input 포커스 중이면 OS 기본 undo에 맡김 (return 없이 이후 로직도 스킵)
        return;
      }

      // modifier 키(Ctrl·Alt·Meta·Fn) 조합은 OS/브라우저 기본 단축키와 충돌 방지를 위해 무시
      if (e.ctrlKey || e.altKey || e.metaKey) return;

      // 입력 영역 포커스 중에는 Esc를 제외한 모든 단축키 비활성
      if (isInputFocused() && e.key !== 'Escape') return;

      // chord 대기 중 — 두 번째 키 처리
      if (chordActiveRef.current) {
        clearChord();
        switch (e.key) {
          case 'h':
            e.preventDefault();
            navigate('/');
            break;
          case 't':
            e.preventDefault();
            navigate('/tags');
            break;
          default:
            // 인식 못 한 chord 조합 — 무시
            break;
        }
        return;
      }

      // 단일 키 처리
      switch (e.key) {
        case 'g':
          // chord 시작 — 1초 대기
          chordActiveRef.current = true;
          chordTimerRef.current = setTimeout(clearChord, CHORD_TIMEOUT_MS);
          break;

        case '/':
          e.preventDefault();
          window.dispatchEvent(new CustomEvent(SHORTCUT_EVENT_FOCUS_SEARCH));
          break;

        case 'Escape':
          // 입력 영역 포커스 중에도 Esc는 동작 (모달 닫기 등)
          window.dispatchEvent(new CustomEvent(SHORTCUT_EVENT_CLOSE_OVERLAY));
          break;

        case '?':
          e.preventDefault();
          window.dispatchEvent(new CustomEvent(SHORTCUT_EVENT_OPEN_SHORTCUTS_MODAL));
          break;

        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      clearChord();
    };
  }, [navigate]);
}
